/**
 * 安心购 / 二手鉴别报告「鉴别意见」文案与分支判断。
 * 与 {@link ../components/jb-report-secondhand/jb-report-secondhand.vue} 保持一致，供订单详情等复用。
 */

/** 分项结论：仅接受 1 / 2 */
export function normInspectDim(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (n === 1 || n === 2) return n;
  return null;
}

export function isLiqAppService(inspectionServiceType) {
  return String(inspectionServiceType || "").trim() === "liq_app";
}

/** 解析 xw_order.jbService（JSON 字符串或对象） */
export function parseXwJbService(order) {
  const d = order || {};
  const raw = d.jbService != null && d.jbService !== "" ? d.jbService : d.jb_service;
  if (raw == null || raw === "") return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(String(raw));
  } catch (e) {
    return null;
  }
}

/** xw_order：是否为「外观+液体」档（jbService id=1 或缺省） */
export function isXwLiqAppService(order) {
  const jb = parseXwJbService(order);
  if (!jb) return true;
  const id = jb.id != null ? Number(jb.id) : null;
  if (id === 2) return false;
  if (id === 1 || id === 3) return true;
  const name = String(jb.name || "").trim();
  if (name.includes("外观鉴别") && !name.includes("液体")) return false;
  if (name.includes("分装") && name.includes("液体")) return true;
  return true;
}

/** xw_order：是否为「仅外观」档 */
export function isXwAppOnlyService(order) {
  return !isXwLiqAppService(order);
}

/** 是否为「仅外观鉴别」档（安心购 app_only 或 xw_order jbService 仅外观） */
export function isAppOnlyInspectionService(order) {
  const d = order || {};
  if (String(d.inspectionServiceType || "").trim() === "app_only") return true;
  return isXwAppOnlyService(d);
}

/**
 * 解析样品分装类型：0 分装，1 不分装。
 * 优先订单 sffz，其次 inspectionReportJson.meta.sffz。
 */
export function resolveSampleSffz(order) {
  const d = order || {};
  if (d.sffz != null && d.sffz !== "") {
    const n = Number(d.sffz);
    if (n === 0 || n === 1) return n;
  }
  const raw = d.inspectionReportJson != null ? d.inspectionReportJson : d.inspection_report_json;
  if (raw == null || raw === "") return null;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (parsed && parsed.meta && parsed.meta.sffz != null && parsed.meta.sffz !== "") {
      const n = Number(parsed.meta.sffz);
      if (n === 0 || n === 1) return n;
    }
  } catch (e) {
    /* ignore */
  }
  return null;
}

/**
 * 分装样品：无实物图、不做外观鉴别（后端会默认写入外观通过，报告页不应展示外观结论）。
 * 判定：sffz=0，或 jbService 为「分装液体」档（id=3）；sffz=1 明确为不分装。
 */
export function isDecantedSample(order) {
  const d = order || {};
  const sffz = resolveSampleSffz(d);
  if (sffz === 0) return true;
  if (sffz === 1) return false;
  const jb = parseXwJbService(d);
  if (jb && jb.id != null && Number(jb.id) === 3) return true;
  const name = jb ? String(jb.name || "").trim() : "";
  return /分装\s*液体/.test(name);
}

/** 报告页样品图区无图时的说明文案（按 sffz 区分分装/不分装，不以有无图片推断） */
export function reportSamplePictureEmptyText(order, context) {
  const ctx = context === "secondhand" ? "secondhand" : "normal";
  if (isDecantedSample(order)) {
    return ctx === "secondhand"
      ? "分装无实物图"
      : "客户以分装形式提供样品，无实物图";
  }
  return ctx === "secondhand" ? "暂无样品图片" : "暂无实物图";
}

/** 综合结论为 3：不符退回（未出具鉴别报告，平台不支持鉴别） */
export function opinionMismatchFromResult(inspectionResult) {
  return Number(inspectionResult) === 3;
}

export function pickInspectionIssueDesc(order) {
  const d = order || {};
  const direct = d.inspectionIssueDesc != null ? d.inspectionIssueDesc : d.inspection_issue_desc;
  if (direct != null && String(direct).trim()) {
    return String(direct).trim();
  }
  const raw = d.inspectionReportJson != null ? d.inspectionReportJson : d.inspection_report_json;
  if (raw == null || raw === "") return "";
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== "object") return "";
    const issue = parsed.issue;
    if (issue && typeof issue === "object") {
      const desc = issue.desc != null ? issue.desc : issue.description;
      if (desc != null && String(desc).trim()) return String(desc).trim();
    }
  } catch (e) {
    /* ignore */
  }
  return "";
}

/** 后台是否录入了具体退回原因（非空即展示） */
export function hasInspectionMismatchSavedReason(order) {
  return !!pickInspectionIssueDesc(order);
}

/** 不符退回主标题：强调未做鉴别 */
export function inspectionMismatchHeadline() {
  return "不支持鉴别，不符退回";
}

/** 不符退回原因（后台录入的 issueDesc 或默认说明） */
export function inspectionMismatchReasonLine(order) {
  const desc = pickInspectionIssueDesc(order);
  return desc || "商品信息与下单描述不一致，或当前品类暂不支持鉴别";
}

/** 订单详情「处理说明」卡正文（不含退回原因，原因单独展示） */
export function inspectionMismatchBodyDesc(order) {
  void order;
  return "鉴别中心收到商品后，无法出具鉴别报告，将按规则为买家办理退款并为卖家寄回商品。";
}

/** 不符退回：退回原因展示文案（有录入则返回，否则 null） */
export function inspectionMismatchReasonDisplay(order) {
  const desc = pickInspectionIssueDesc(order);
  return desc || null;
}

/** 不符退回关单：退款说明 */
export function inspectionMismatchRefundNote() {
  return "实付金额（含运费及安心购服务费）已由平台原路全额退回买家";
}

/** 顶栏 / 结论卡入口文案 */
export function inspectionReportEntryLabel(order) {
  return opinionMismatchFromResult(order && order.inspectionResult)
    ? "查看处理说明"
    : "查看鉴别报告";
}

export function inspectionConclusionCardTitle(order) {
  return opinionMismatchFromResult(order && order.inspectionResult) ? "处理说明" : "验货结论";
}

export function inspectionConclusionDetailLabel(order) {
  return opinionMismatchFromResult(order && order.inspectionResult) ? "查看详情" : "查看报告";
}

function inspectPassFailWord(n) {
  if (n === 1) return "通过";
  if (n === 2) return "不通过";
  return null;
}

/** 订单详情 headline：香水外观鉴别通过 / 不通过 */
export function appearanceOpinionLine(n) {
  const w = inspectPassFailWord(n);
  return w ? `香水外观鉴别${w}` : "—";
}

/** 订单详情 headline：香水液体鉴别通过 / 不通过 */
export function liquidOpinionLine(n) {
  const w = inspectPassFailWord(n);
  return w ? `香水液体鉴别${w}` : "—";
}

/**
 * 订单详情 headline（liq_app 双分项）：逗号连接，如
 * 香水外观鉴别通过，香水液体鉴别不通过
 */
export function dualOpinionLine(appearanceNum, liquidNum) {
  const aw = inspectPassFailWord(appearanceNum);
  const lw = inspectPassFailWord(liquidNum);
  if (!aw || !lw) return "—";
  return `香水外观鉴别${aw}；香水液体鉴别${lw}`;
}

/** 报告页鉴别意见（工艺/图谱文案，订单详情不用） */
export function appearanceOpinionDesc(n) {
  if (n === 1) return "香水外观符合正品工艺";
  if (n === 2) return "香水外观不符合正品工艺";
  return "—";
}

export function appearanceOpinionVerdict(n) {
  if (n === 1) return "鉴别通过";
  if (n === 2) return "鉴别不通过";
  return "—";
}

export function liquidOpinionDesc(n) {
  if (n === 1) return "香水液体指纹图谱相似度>95%";
  if (n === 2) return "香水液体指纹图谱相似度<95%";
  return "—";
}

export function liquidOpinionVerdict(n) {
  if (n === 1) return "鉴别通过";
  if (n === 2) return "鉴别不通过";
  return "—";
}

export function splitAppearanceNum(order) {
  const d = order || {};
  return normInspectDim(
    d.inspectionResultAppearance != null && d.inspectionResultAppearance !== ""
      ? d.inspectionResultAppearance
      : d.inspection_result_appearance
  );
}

/** xw_order 历史：jbjg（0 假 / 1 真）映射为液体分项 2 / 1 */
export function xwJbjgToLiquidNum(order) {
  const d = order || {};
  const j = d.jbjg;
  if (j == null || j === "") return null;
  const jn = Number(j);
  if (jn === 1) return 1;
  if (jn === 0) return 2;
  return null;
}

export function splitLiquidNum(order) {
  const d = order || {};
  const fromField = normInspectDim(
    d.inspectionResultLiquid != null && d.inspectionResultLiquid !== ""
      ? d.inspectionResultLiquid
      : d.inspection_result_liquid
  );
  if (fromField != null) return fromField;
  if (isXwAppOnlyService(d)) return null;
  // 外观不通过且液体未分项：勿用 jbjg 兜底（避免谱图区误走「液体不通过」安全提示）
  if (splitAppearanceNum(d) === 2) return null;
  return xwJbjgToLiquidNum(d);
}

/**
 * 样品鉴别图谱遮罩：液体不通过仍展示谱图。
 * 仅「外观鉴别」档不展示样品谱图（整段图谱区亦由 showInspectionChartsSection 控制）。
 */
export function shouldHideSampleIdentifyChart(order) {
  return isAppOnlyInspectionService(order || {});
}

/** 报告页「订单编号」：二手 orderNo，鉴别单 orderId */
export function reportOrderNoText(order) {
  const d = order || {};
  const orderNo = d.orderNo != null && d.orderNo !== "" ? d.orderNo : d.order_no;
  if (orderNo != null && String(orderNo).trim()) return String(orderNo).trim();
  const orderId = d.orderId != null && d.orderId !== "" ? d.orderId : d.order_id;
  if (orderId != null && String(orderId).trim()) return String(orderId).trim();
  return "—";
}

/**
 * 香水鉴别报告顶栏角标：是否显示「鉴别不通过」。
 * 外观+液体任一项不通过即为不通过，不能只看液体分项。
 */
export function reportInspectionFailBadge(order) {
  const d = order || {};
  if (opinionMismatchFromResult(d.inspectionResult)) {
    return false;
  }
  const appearance = splitAppearanceNum(d);
  const liquid = splitLiquidNum(d);
  if (appearance === 2) return true;
  if (liquid === 2) return true;
  if (showOpinionDualColumn(d)) {
    return !(appearance === 1 && liquid === 1);
  }
  if (showOpinionAppearanceCard(d)) {
    const a = singleAppearanceNum(d);
    if (a === 2) return true;
    if (a === 1) return false;
  }
  if (showOpinionLiquidOnlyCard(d)) {
    if (liquid === 1) return false;
    if (liquid === 2) return true;
  }
  return xwJbjgToLiquidNum(d) === 2;
}

/** 安心购报告顶栏角标：是否显示「鉴别不通过」 */
export function reportSecondhandFailBadge(order) {
  const d = order || {};
  if (opinionMismatchFromResult(d.inspectionResult)) {
    return false;
  }
  const overall = d.inspectionResult;
  if (overall != null && overall !== "") {
    const n = Number(overall);
    if (n === 2) return true;
    if (n === 1) return false;
  }
  if (splitAppearanceNum(d) === 2) return true;
  if (splitLiquidNum(d) === 2) return true;
  return false;
}

/** 分项外观、液体均为 1/2 时双栏展示（安心购 liq_app 或 xw_order 外观+液体） */
export function showOpinionDualColumn(order) {
  const d = order || {};
  if (isDecantedSample(d)) return false;
  const liqApp = isLiqAppService(d.inspectionServiceType) || isXwLiqAppService(d);
  if (!liqApp) return false;
  return splitAppearanceNum(d) != null && splitLiquidNum(d) != null;
}

/** 单栏外观：仅分项字段；仅外观档可用 jbjg / 综合结论兜底（勿把液体 jbjg 当外观） */
export function singleAppearanceNum(order) {
  const d = order || {};
  const a = splitAppearanceNum(d);
  if (a != null) return a;
  if (!isXwAppOnlyService(d)) {
    const n = Number(d.inspectionResult);
    if (n === 1 || n === 2) return n;
    return null;
  }
  return xwJbjgToLiquidNum(d);
}

export function showOpinionAppearanceCard(order) {
  if (isDecantedSample(order)) return false;
  if (opinionMismatchFromResult(order && order.inspectionResult)) return false;
  if (showOpinionDualColumn(order)) return false;
  if (splitAppearanceNum(order) != null) return true;
  return isXwAppOnlyService(order) && singleAppearanceNum(order) != null;
}

/** xw_order 报告：历史液体单（无外观分项、非仅外观档） */
export function showOpinionLiquidOnlyCard(order) {
  const d = order || {};
  if (opinionMismatchFromResult(d.inspectionResult)) return false;
  if (showOpinionDualColumn(d)) return false;
  if (showOpinionAppearanceCard(d)) return false;
  if (isXwAppOnlyService(d)) return false;
  return splitLiquidNum(d) != null;
}

/** 1 真品 2 假货 3 不符退回（与报告页 opinionMain 一致） */
export function opinionMainFromResult(order) {
  const d = order || {};
  const r = d.inspectionResult;
  if (r == null || r === "") return "—";
  const n = Number(r);
  if (n === 1) return "鉴别为真品（通过）";
  if (n === 2) return "鉴别为假货（不通过）";
  if (n === 3) return inspectionMismatchHeadline();
  return "—";
}

/**
 * 订单详情「验货结论 / 处理说明」主标题。
 * - 不符退回：不支持鉴别（非鉴别报告结论）
 * - liq_app 且外观+液体分项齐全：dualOpinionLine
 * - 仅外观分项或综合 1/2 兜底：appearanceOpinionLine
 * - 其它：opinionMainFromResult
 */
export function inspectionOpinionHeadline(order) {
  const o = order || {};
  if (opinionMismatchFromResult(o.inspectionResult)) {
    return inspectionMismatchHeadline();
  }
  if (showOpinionDualColumn(o)) {
    return dualOpinionLine(splitAppearanceNum(o), splitLiquidNum(o));
  }
  if (showOpinionAppearanceCard(o)) {
    return appearanceOpinionLine(singleAppearanceNum(o));
  }
  if (isDecantedSample(o) && splitLiquidNum(o) != null) {
    return liquidOpinionLine(splitLiquidNum(o));
  }
  return opinionMainFromResult(o);
}



// WEBPACK FOOTER //
// ./src/util/secondhandInspectionOpinion.js